import {
  DEADLINE_CONTROL_MODES,
  type DeadlineControlMode,
  type DeadlineControlSettings,
  type DeadlineControlView,
  DIGEST_HOURS,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const KEY = ['admin', 'deadline-control'];

const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;

function plural(n: number, one: string, few: string, many: string): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return `${n} ${one}`;
  if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) return `${n} ${few}`;
  return `${n} ${many}`;
}

/**
 * Контроль сроков по команде (ЗД-13): каждый день руководитель проекта получает письмо — что просрочено у каждого
 * сотрудника его команды, на сколько дней и ссылка на запись. Первый запуск — черновиком владельцу компании.
 */
export function DeadlineControlPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<DeadlineControlView>('/admin/deadline-control') });
  const [form, setForm] = useState<DeadlineControlSettings | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(q.data.settings);
  }, [q.data, form]);
  const save = useMutation({
    mutationFn: (body: DeadlineControlSettings) =>
      api<DeadlineControlView>('/admin/deadline-control', { method: 'PUT', body }),
    onSuccess: (x) => {
      qc.setQueryData(KEY, x);
      setForm(x.settings);
      toast('Контроль сроков сохранён');
    },
  });
  const draft = useMutation({
    mutationFn: () =>
      api<{ letters: number; sent: number; failed: number }>('/admin/deadline-control/draft', {
        method: 'POST',
      }),
    onSuccess: (r) =>
      r.sent
        ? toast(`Черновик отправлен вам: ${plural(r.letters, 'письмо', 'письма', 'писем')}`)
        : r.failed
          ? toast('Почта не приняла письмо — попробуйте позже', 'danger')
          : toast('Просрочек в командах нет — черновик пустой и не отправлен'),
  });

  return (
    <Panel title="Контроль сроков по команде" meta="ЗД-13; изменения — в «Истории изменений»">
      <div className="flex max-w-2xl flex-col gap-4 text-[14px]">
        <p className="text-ink-2">
          Раз в день руководитель проекта получает письмо по каждому сотруднику команды своего проекта: какие
          поручения просрочены, на сколько дней и ссылка на запись. Если просрочек нет, письма нет.
          Руководитель — из карточки проекта, команда — сотрудники с ролью на проект или его подпроект. Каждый
          руководитель может отказаться от письма в своём профиле (Профиль → Уведомления). Суммы в письма не
          попадают.
        </p>
        {q.isPending || !form ? (
          q.isError ? (
            <ErrorBox error={q.error} />
          ) : (
            <Spinner />
          )
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Режим">
                <Select
                  value={form.mode}
                  onChange={(e) => setForm({ ...form, mode: e.target.value as DeadlineControlMode })}
                >
                  {Object.entries(DEADLINE_CONTROL_MODES).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Час отправки (по часам компании)">
                <Select
                  value={String(form.hour)}
                  onChange={(e) =>
                    setForm({ ...form, hour: Number(e.target.value) as DeadlineControlSettings['hour'] })
                  }
                >
                  {DIGEST_HOURS.map((h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {form.mode === 'draft' ? (
              <p className="text-ink-2">
                Сейчас письма приходят черновиком владельцу компании одним письмом, руководителям ничего не
                уходит. Чтобы письма уходили руководителям, выберите «Письма руководителям проектов».
              </p>
            ) : null}
            {!q.data.mailConfigured ? (
              <p className="text-danger">Почта портала не настроена — письма не отправляются.</p>
            ) : null}
            {q.data.isDemo ? (
              <p className="text-ink-2">В демо-компании письма не отправляются — ниже видно, что ушло бы.</p>
            ) : null}
            <ErrorBox error={save.error ?? draft.error} />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={JSON.stringify(form) === JSON.stringify(q.data.settings)}
                onClick={() => save.mutate(form)}
              >
                Сохранить
              </Button>
              {q.data.previewAllowed ? (
                <Button
                  loading={draft.isPending}
                  disabled={!q.data.mailConfigured || q.data.isDemo}
                  onClick={() => draft.mutate()}
                >
                  Прислать черновик мне
                </Button>
              ) : null}
            </div>
            {q.data.lastRun ? (
              <p className="text-ink-2">
                Последняя отправка: {q.data.lastRun.date}
                {q.data.lastRun.mode === 'draft' ? ', черновик' : ''},{' '}
                {plural(q.data.lastRun.letters, 'письмо', 'письма', 'писем')}, доставлено{' '}
                {q.data.lastRun.sent}
                {q.data.lastRun.failed ? `, не доставлено ${q.data.lastRun.failed}` : ''}
                {q.data.lastRun.finishedAt ? ` (${dateTime(q.data.lastRun.finishedAt)})` : ''}.
              </p>
            ) : null}
            {!q.data.previewAllowed ? (
              <p className="border-t border-line pt-4 text-ink-2">
                Какие письма уйдут сегодня, видит и получает черновиком только владелец компании: в письмах
                дела всех команд всех проектов.
              </p>
            ) : (
              <section className="border-t border-line pt-4">
                {q.data.skipped.length ? (
                  <div className="mb-3">
                    <h3 className="mb-1 text-[13px] font-medium text-ink">Письмо не уйдёт</h3>
                    <ul className="flex flex-col gap-1 pl-4">
                      {q.data.skipped.map((x) => (
                        <li key={`${x.projectCode}-${x.managerName}`} className="list-disc text-ink-2">
                          {x.projectCode}: {x.managerName} — {x.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <h3 className="mb-2 text-[13px] font-medium text-ink">
                  Что ушло бы сегодня: {plural(q.data.letters.length, 'письмо', 'письма', 'писем')}
                </h3>
                {!q.data.letters.length ? (
                  <p className="text-ink-2">Просрочек в командах проектов нет — писем не будет.</p>
                ) : (
                  <ul className="flex flex-col gap-3">
                    {q.data.letters.map((l) => (
                      <li key={l.managerId} className="rounded-md border border-line">
                        <details>
                          <summary className="flex min-h-11 cursor-pointer flex-wrap items-center gap-x-2 px-3 py-2">
                            <span className="font-medium text-ink">{l.managerName}</span>
                            <span className="text-ink-2">
                              —{' '}
                              {plural(
                                l.itemsCount,
                                'просроченное дело',
                                'просроченных дела',
                                'просроченных дел',
                              )}
                            </span>
                          </summary>
                          <div className="flex flex-col gap-3 border-t border-line px-3 py-2">
                            {l.projects.map((p) => (
                              <div key={p.projectId}>
                                <p className="font-medium text-ink">
                                  {p.code} · {p.name}
                                </p>
                                {p.members.map((m) => (
                                  <div key={m.userId} className="mt-2">
                                    <p className="text-ink">
                                      {m.fullName}{' '}
                                      <span className="text-danger">просрочено {m.items.length}</span>
                                    </p>
                                    <ul className="mt-1 flex flex-col gap-1 pl-4">
                                      {m.items.map((i) => (
                                        <li key={i.id} className="list-disc">
                                          <a
                                            href={i.path}
                                            className="inline-flex min-h-11 items-center underline underline-offset-2 [overflow-wrap:anywhere] md:min-h-0"
                                          >
                                            {i.title}
                                          </a>{' '}
                                          <span className="text-danger">
                                            {plural(i.daysOverdue, 'день', 'дня', 'дней')}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </details>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}
